/**
 * HUB-C — Actions paiements (confirm/cancel/ticket/preuves) sans changement métier backend.
 */
import { useCallback, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

export default function useActivitePaiementActions({ api, onRefresh }) {
  const { tError } = useLanguage();
  const [confirming, setConfirming] = useState(null);
  const [cancelling, setCancelling] = useState(null);

  const confirmPayment = useCallback(async (checkoutId) => {
    setConfirming(checkoutId);
    try {
      await api.post(`/payments/sumup/staff-confirm/${checkoutId}`);
      await onRefresh();
    } catch (err) {
      alert(tError(err.response?.data?.detail) || 'Erreur lors de la confirmation');
    } finally {
      setConfirming(null);
    }
  }, [api, onRefresh, tError]);

  const cancelPayment = useCallback(async (checkoutId) => {
    if (!window.confirm('Annuler ce paiement ?')) return;
    setCancelling(checkoutId);
    try {
      await api.post(`/payments/sumup/cancel/${checkoutId}`);
      await onRefresh();
    } catch (err) {
      alert(tError(err.response?.data?.detail) || 'Erreur');
    } finally {
      setCancelling(null);
    }
  }, [api, onRefresh, tError]);

  const openTicket = useCallback(async (checkoutId) => {
    try {
      const response = await api.get(`/payments/sumup/ticket/${checkoutId}`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank', 'width=900,height=700');
      if (printWindow) {
        printWindow.addEventListener('load', () => { printWindow.focus(); printWindow.print(); });
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 8000);
    } catch {
      alert('Erreur lors du téléchargement du ticket');
    }
  }, [api]);

  const viewProof = useCallback(async (orderId, fileId) => {
    try {
      const response = await api.get(`/boutique/commandes/${orderId}/payment-proof/${fileId}`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'image/jpeg' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
    } catch {
      alert('Erreur lors de l\'ouverture de la preuve');
    }
  }, [api]);

  const validateProof = useCallback(async (orderId) => {
    if (!window.confirm('Valider cette preuve de paiement ?')) return;
    try {
      await api.post(`/boutique/commandes/${orderId}/validate-proof`);
      await onRefresh();
    } catch (err) {
      alert(tError(err.response?.data?.detail) || 'Erreur');
    }
  }, [api, onRefresh, tError]);

  const rejectProof = useCallback(async (orderId) => {
    if (!window.confirm('Refuser cette preuve de paiement ?')) return;
    try {
      await api.post(`/boutique/commandes/${orderId}/reject-proof`);
      await onRefresh();
    } catch (err) {
      alert(tError(err.response?.data?.detail) || 'Erreur');
    }
  }, [api, onRefresh, tError]);

  const confirmOrder = useCallback(async (orderId) => {
    if (!window.confirm('Confirmer cette commande ?')) return;
    try {
      await api.put(`/boutique/commandes/${orderId}/status?status=confirmee`);
      await onRefresh();
    } catch (err) {
      alert(tError(err.response?.data?.detail) || 'Erreur');
    }
  }, [api, onRefresh, tError]);

  return {
    confirming,
    cancelling,
    confirmPayment,
    cancelPayment,
    openTicket,
    viewProof,
    validateProof,
    rejectProof,
    confirmOrder,
  };
}
